import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export interface User {
  readonly id: number;
  readonly username: string;
  readonly email: string;
  readonly role: 'jogador' | 'moderador' | 'admin' | 'dono';
  readonly avatarPortrait: string;
  readonly bio: string | null;
  readonly createdAt: string;
}

export interface PlayerStats {
  readonly skirmishesPlayed: number;
  readonly skirmishesWon: number;
  readonly winRate: number;
  readonly eloRating: number;
  readonly favoriteClass: string;
}

interface AuthContextValue {
  readonly user: User | null;
  readonly stats: PlayerStats | null;
  readonly loading: boolean;
  readonly login: (identity: string, pass: string) => Promise<{ ok: boolean; message?: string }>;
  readonly register: (username: string, email: string, pass: string) => Promise<{ ok: boolean; message?: string }>;
  readonly logout: () => Promise<void>;
  readonly updateProfile: (data: { avatarPortrait?: string; bio?: string; favoriteClass?: string }) => Promise<boolean>;
  readonly recordMatch: (outcome: 'vitoria' | 'derrota') => Promise<void>;
  readonly refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as {
        authenticated: boolean;
        user: User | null;
        stats: PlayerStats | null;
      };
      if (data.authenticated && data.user) {
        setUser(data.user);
        setStats(data.stats);
      } else {
        setUser(null);
        setStats(null);
      }
    } catch {
      setUser(null);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchMe();
  }, []);

  const login = async (identity: string, pass: string) => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ login: identity, password: pass }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { ok: false, message: data.error?.message ?? 'Falha ao autenticar.' };
      }
      setUser(data.user);
      setStats(data.stats);
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : String(e) };
    }
  };

  const register = async (username: string, email: string, pass: string) => {
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, email, password: pass }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { ok: false, message: data.error?.message ?? 'Falha ao registrar conta.' };
      }
      setUser(data.user);
      setStats(data.stats);
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : String(e) };
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } finally {
      setUser(null);
      setStats(null);
    }
  };

  const updateProfile = async (data: { avatarPortrait?: string; bio?: string; favoriteClass?: string }) => {
    try {
      const res = await fetch('/api/users/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) return false;
      const resData = await res.json();
      if (resData.user) setUser(resData.user);
      if (resData.stats) setStats(resData.stats);
      return true;
    } catch {
      return false;
    }
  };

  const recordMatch = async (outcome: 'vitoria' | 'derrota') => {
    if (!user) return;
    try {
      const res = await fetch('/api/matches/record', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ outcome, mode: 'vs_cpu' }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.stats) {
        setStats(data.stats);
      }
    } catch {
      // Falhas silenciosas de telemetria
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        stats,
        loading,
        login,
        register,
        logout,
        updateProfile,
        recordMatch,
        refresh: fetchMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return ctx;
}

